// packages/web/src/context/AuthContext.tsx
import React, { createContext, useContext, useState } from 'react'
import { AUTH_TOKEN_KEY, AUTH_USER_KEY, clearAuthStorage, writeAuthSession } from '../lib/authStorage'

export interface User {
  id: string
  name?: string | null
  phone?: string | null
  email?: string | null
  role: 'customer' | 'admin' | 'superadmin'
}

interface AuthContextType {
  user: User | null
  token: string | null
  login: (token: string, user: User) => void
  logout: () => void
  isAuthenticated: boolean
  isAdmin: boolean
  isCustomer: boolean
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [token, setToken] = useState<string | null>(() => {
    try {
      return localStorage.getItem(AUTH_TOKEN_KEY)
    } catch {
      return null
    }
  })
  const [user, setUser] = useState<User | null>(() => {
    try {
      const saved = localStorage.getItem(AUTH_USER_KEY)
      return saved ? JSON.parse(saved) : null
    } catch {
      return null
    }
  })

  const login = (newToken: string, newUser: User) => {
    setToken(newToken)
    setUser(newUser)
    writeAuthSession(newToken, JSON.stringify(newUser))
  }

  const logout = () => {
    const snapshot = token
    setToken(null)
    setUser(null)
    clearAuthStorage()

    if (!snapshot) return
    const apiBase = import.meta.env.VITE_API_URL || '/api'
    void fetch(`${apiBase.replace(/\/$/, '')}/auth/logout`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${snapshot}` },
    }).catch(() => {
      // Session revoke is best-effort; storage is already cleared.
    })
  }

  const isAuthenticated = !!token && !!user
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin'
  const isCustomer = user?.role === 'customer'

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        login,
        logout,
        isAuthenticated,
        isAdmin,
        isCustomer,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
